import React, { useState, useEffect, useRef } from "react";
import "./ApplicationFormView.scss";
import SuccessDialog from "./SuccessDialog";
import Logo from "../../iamges/logo.png";
import { useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";

const ApplicationFormView: React.FC = () => {
  const { id } = useParams<{ id: string; type?: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get("type");
  const [isSticky, setIsSticky] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const [step, setStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    adultsCount: 1,
    adultNames: "",
    email: "",
    phone: "",
    moveInDate: "",
    moveReason: "",
    hasPets: false,
    petDetails: "",
    income: "",
    entitledToRent: false,
    consentCheck: false,
    backgroundConsent: true,
  });

  useEffect(() => {
    const handleScroll = () => {
      if (boxRef.current) {
        const scrollTop = boxRef.current.scrollTop;
        if (scrollTop > 10) {
          setIsSticky(true);
        } else {
          setIsSticky(false);
        }
      }
    };

    const box = boxRef.current;
    if (box) {
      box.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (box) {
        box.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    if (boxRef.current) {
      boxRef.current.scrollTo({
        top: 0,
        behavior: "smooth", // optional: smooth scrolling
      });
    }
  }, [step]);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" })); // clear error when user types
  };

  /** ✅ Step 1 Validation */
  const validateStep1 = () => {
    let newErrors: any = {};

    if (!formData.adultNames.trim())
      newErrors.adultNames = "Full legal name(s) are required.";

    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else {
      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
      if (formData.email.length > 254 || !emailRegex.test(formData.email)) {
        newErrors.email = "Please enter a valid email address.";
      }
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required.";
    } else {
      // ✅ Canadian number: allows 10 digits or +1 followed by 10 digits
      const canadianPhoneRegex = /^(?:\+1)?[2-9]\d{2}[2-9]\d{6}$/;
      if (!canadianPhoneRegex.test(formData.phone)) {
        newErrors.phone =
          "Enter a valid Canadian phone number (e.g., 4165551234 or +14165551234).";
      }
    }
    if (!formData.moveInDate.trim())
      newErrors.moveInDate = "Move-in date is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /** ✅ Step 2 Validation */
  const validateStep2 = () => {
    let newErrors: any = {};
    if (formData.hasPets && !formData.petDetails.trim()) {
      newErrors.petDetails = "Please provide details about your pet(s).";
    }

    // 🔴 Make income required
    if (!String(formData.income).trim()) {
      newErrors.income = "Monthly household income is required.";
    }

    if (!formData.consentCheck) {
      newErrors.consentCheck = "You must consent to proceed.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) setStep(2);
  };

  const handlePrevious = () => setStep(1);

  const handleSubmit = async () => {
    if (!validateStep2()) return;

    try {
      setLoading(true);

      const payload = {
        propertyId: id, // Replace with dynamic ID if needed
        numberOfOccupants: formData.adultsCount, // 1
        fullLegalName: formData.adultNames, // 2
        emailAddress: formData.email, // 3
        phoneNumber: formData.phone, // 4
        preferredMoveInDate: formData.moveInDate, // 5
        currentOccupation: formData.moveReason || "N/A", // 6
        hasPets: formData.hasPets, // 7
        petType: formData.hasPets ? formData.petDetails : "", // 7.1
        monthlyIncome: formData.income, // 8
        canadaStatus: formData.entitledToRent, // 9
        consentToCreditBackgroundCheck: formData.backgroundConsent, // 10
        employerName: "N/A",
        employmentDuration: "N/A",
        positionTitle: "N/A",
        annualIncome: 0,
        employmentType: "full-time",
        additionalNotes: "Submitted via HomeOwner App",
      };

      const url =
        type === "staging"
          ? "https://homeowner-staging.azurewebsites.net/api/rental-inquiries"
          : "https://homeowner-backend.azurewebsites.net/api/rental-inquiries";

      await axios.post(url, payload, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      setShowSuccess(true);
    } catch (err: any) {
      toast(err.message, {
        type: "error",
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen full-page-bg flex items-center justify-center bg-gradient-to-br from-purple-50 to-white'>
      <div
        ref={boxRef}
        className='white-box rounded-xl shadow-lg w-full max-w-2xl pt-4 pb-8'>
        {/* 🔹 Stepper */}
        <div
          className={`flex justify-between items-center pt-4 pb-4 top-fixed-header left-right-space ${
            isSticky ? "sticky-active" : ""
          }`}>
          <img src={Logo} alt='HomeOwner Logo' className='w-12 h-12' />
          <div className='flex justify-center'>
            {[1, 2].map((n) => (
              <div key={n} className='flex items-center'>
                <div
                  className={`w-8 h-8 flex items-center justify-center rounded-full ${
                    step === n
                      ? "bg-[#F3943F] text-white"
                      : "bg-gray-200 text-gray-500"
                  }`}>
                  {n}
                </div>
                {n !== 2 && <div className='w-10 h-1 bg-gray-300 mx-2'></div>}
              </div>
            ))}
          </div>
        </div>
        <div className='left-right-space'>
          {/* 🔹 Header */}
          <h2 className='text-xl font-semibold mb-3'>
            {step === 1 ? "Apply for this Rental Property" : "Rental Details"}
          </h2>
          {step === 1 ? (
            <>
              <p className='text-gray-500 mb-3'>
                This property is managed through the <b>HomeOwner App</b>.
              </p>
              <p className='text-gray-500 mb-3'>
                Your details will be securely shared with the landlord so they
                can connect with you directly.
              </p>
              <p className='text-gray-500 mb-3'>
                This helps speed up the rental process and keeps all
                communication in one place.
              </p>
            </>
          ) : (
            <p className='text-gray-500 mb-6'>
              Just a few more details to complete your pre-screening.
            </p>
          )}

          <div className='form-parts mb-8'>
            {/* ✅ STEP 1 */}
            {step === 1 && (
              <div className='space-y-5'>
                {/* Adults Count */}
                <div>
                  <label
                    htmlFor='livingMember'
                    className='block mb-1 font-medium'>
                    1. Adults (18+) living in property
                  </label>
                  <div className='flex items-center border rounded-lg w-60'>
                    <button
                      type='button'
                      className='px-4 py-2 text-white bg-[#e6842e] hover:bg-[#cc7327] rounded-l-lg'
                      onClick={() =>
                        handleChange(
                          "adultsCount",
                          Math.max(1, formData.adultsCount - 1)
                        )
                      }>
                      –
                    </button>
                    <input
                      type='text'
                      readOnly
                      value={formData.adultsCount}
                      className='w-full text-center outline-none height-40'
                      id='livingMember'
                    />
                    <button
                      type='button'
                      className='px-4 py-2 rounded-r-lg text-white bg-[#e6842e] hover:bg-[#cc7327]'
                      onClick={() =>
                        handleChange("adultsCount", formData.adultsCount + 1)
                      }>
                      +
                    </button>
                  </div>
                </div>

                {/* Names */}
                <div>
                  <label
                    htmlFor='user-names'
                    className='block mb-1 font-medium'>
                    2. Full legal name(s) of adults{" "}
                    <span className='text-red-500'>*</span>
                  </label>
                  <textarea
                    id='user-names'
                    placeholder='e.g., Jane Doe, John Smith'
                    value={formData.adultNames}
                    onChange={(e) => handleChange("adultNames", e.target.value)}
                    className='w-full border rounded-lg p-2 h-20 focus:ring-2 focus:ring-[#F3943F] outline-none'
                  />
                  {errors.adultNames && (
                    <p className='text-red-500 text-sm'>{errors.adultNames}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor='email' className='block mb-1 font-medium'>
                    3. Contact Email <span className='text-red-500'>*</span>
                  </label>
                  <input
                    id='email'
                    type='email'
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className='w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#F3943F] outline-none'
                  />
                  {errors.email && (
                    <p className='text-red-500 text-sm'>{errors.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label
                    htmlFor='phone-number'
                    className='block mb-1 font-medium'>
                    4. Contact Phone Number{" "}
                    <span className='text-red-500'>*</span>
                  </label>
                  <input
                    id='phone-number'
                    type='tel'
                    value={formData.phone}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (value.startsWith("+")) {
                        value = "+" + value.slice(1).replace(/\D/g, "");
                      } else {
                        value = value.replace(/\D/g, "");
                      }
                      handleChange("phone", value);
                    }}
                    maxLength={12}
                    className='w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#F3943F] outline-none'
                  />
                  {errors.phone && (
                    <p className='text-red-500 text-sm'>{errors.phone}</p>
                  )}
                </div>

                {/* Move-in Date */}
                <div>
                  <label htmlFor='date' className='block mb-1 font-medium'>
                    5. Desired Move-in Date{" "}
                    <span className='text-red-500'>*</span>
                  </label>
                  <input
                    id='date'
                    type='date'
                    value={formData.moveInDate}
                    onChange={(e) => handleChange("moveInDate", e.target.value)}
                    className='w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#F3943F] outline-none'
                  />
                  {errors.moveInDate && (
                    <p className='text-red-500 text-sm'>{errors.moveInDate}</p>
                  )}
                </div>

                {/* Reason */}
                <div>
                  <label htmlFor='reason' className='block mb-1 font-medium'>
                    6. Reason for moving
                  </label>
                  <textarea
                    id='reason'
                    value={formData.moveReason}
                    onChange={(e) => handleChange("moveReason", e.target.value)}
                    className='w-full border rounded-lg p-2 h-20 focus:ring-2 focus:ring-[#F3943F] outline-none'
                  />
                </div>
              </div>
            )}

            {/* ✅ STEP 2 */}
            {step === 2 && (
              <div className='space-y-5'>
                {/* Pets Toggle */}
                <div>
                  <label htmlFor='pets' className='block mb-1 font-medium'>
                    7. Do you have any pets?
                  </label>
                  <div className='flex gap-3'>
                    <button
                      type='button'
                      className={`flex-1 py-2 rounded-lg border ${
                        formData.hasPets
                          ? "bg-[#F3943F] text-white"
                          : "bg-gray-100"
                      }`}
                      onClick={() => handleChange("hasPets", true)}>
                      Yes
                    </button>
                    <button
                      type='button'
                      className={`flex-1 py-2 rounded-lg border ${
                        !formData.hasPets
                          ? "bg-[#F3943F] text-white"
                          : "bg-gray-100"
                      }`}
                      onClick={() => handleChange("hasPets", false)}>
                      No
                    </button>
                  </div>
                </div>

                {formData.hasPets && (
                  <div>
                    <input
                      id='pets'
                      type='text'
                      placeholder='e.g., One small dog, two cats'
                      value={formData.petDetails}
                      onChange={(e) =>
                        handleChange("petDetails", e.target.value)
                      }
                      className='w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#F3943F] outline-none'
                    />
                    {errors.petDetails && (
                      <p className='text-red-500 text-sm'>
                        {errors.petDetails}
                      </p>
                    )}
                  </div>
                )}

                {/* Income */}
                <div>
                  <label htmlFor='income' className='block mb-1 font-medium'>
                    8. What is the total monthly household income (before tax)
                    for all applicants? <span className='text-red-500'>*</span>
                  </label>
                  <input
                    id='income'
                    placeholder='Enter income in CAD'
                    value={formData.income}
                    onChange={(e) => {
                      handleChange("income", e.target.value);
                    }}
                    className='w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#F3943F] outline-none'
                  />
                  {errors.income && (
                    <p className='text-red-500 text-sm'>{errors.income}</p>
                  )}
                </div>

                {/* Consent Toggles */}
                <div>
                  <label htmlFor='legally' className='block font-medium mb-1'>
                    9. Are all applicants legally entitled to rent in Canada?
                  </label>
                  <div className='flex gap-3'>
                    <button
                      className={`flex-1 py-2 rounded-lg border ${
                        formData.entitledToRent
                          ? "bg-[#F3943F] text-white"
                          : "bg-gray-100"
                      }`}
                      onClick={() => handleChange("entitledToRent", true)}>
                      Yes
                    </button>
                    <button
                      className={`flex-1 py-2 rounded-lg border ${
                        !formData.entitledToRent
                          ? "bg-[#F3943F] text-white"
                          : "bg-gray-100"
                      }`}
                      onClick={() => handleChange("entitledToRent", false)}>
                      No
                    </button>
                  </div>
                </div>

                {/* Background Check */}
                <div>
                  <label htmlFor='credit' className='block font-medium mb-1'>
                    10. If you decide to apply after viewing, do you consent to
                    a credit and background check?
                  </label>
                  <div className='flex gap-3'>
                    <button
                      className={`flex-1 py-2 rounded-lg border ${
                        formData.backgroundConsent
                          ? "bg-[#F3943F] text-white"
                          : "bg-gray-100"
                      }`}
                      onClick={() => handleChange("backgroundConsent", true)}>
                      Yes
                    </button>
                    <button
                      className={`flex-1 py-2 rounded-lg border ${
                        !formData.backgroundConsent
                          ? "bg-[#F3943F] text-white"
                          : "bg-gray-100"
                      }`}
                      onClick={() => handleChange("backgroundConsent", false)}>
                      No
                    </button>
                  </div>
                </div>

                {/* Consent Checkbox */}
                <div className='flex items-start gap-2'>
                  <input
                    type='checkbox'
                    checked={formData.consentCheck}
                    onChange={(e) =>
                      handleChange("consentCheck", e.target.checked)
                    }
                    className='mt-1'
                  />
                  <span className='text-sm'>
                    I agree that my details will only be shared securely with
                    the landlord of this property for rental purposes.{" "}
                    <a
                      href='/policy'
                      target='_black'
                      style={{ color: "#f3933f" }}>
                      Privacy Policy
                    </a>
                  </span>
                </div>
                {errors.consentCheck && (
                  <p className='text-red-500 text-sm'>{errors.consentCheck}</p>
                )}
              </div>
            )}
          </div>

          {step === 1 ? (
            <p className='mb-2' style={{ fontSize: "14px" }}>
              🔒 Your details are private and only shared with this landlord.
            </p>
          ) : (
            <p className='mb-2' style={{ fontSize: "14px" }}>
              🔒 Your information is safe and will not be shared outside this
              application.
            </p>
          )}

          {/* 🔹 Buttons */}
          {step === 1 ? (
            <button
              onClick={handleNext}
              className='w-full bg-gradient-to-r from-[#F3943F] to-[#e6842e] text-white py-2 rounded-lg hover:opacity-90 transition'>
              Continue →
            </button>
          ) : (
            <div className='flex justify-between gap-3'>
              <button
                onClick={handlePrevious}
                className='w-1/2 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition'>
                ← Back
              </button>
              <button
                onClick={handleSubmit}
                className='w-1/2 bg-gradient-to-r from-[#F3943F] to-[#e6842e] text-white py-2 rounded-lg hover:opacity-90 transition'>
                {loading ? "Submitting..." : "Submit →"}
              </button>
            </div>
          )}
        </div>
      </div>

      <SuccessDialog
        visible={showSuccess}
        onClose={() => {
          setShowSuccess(false);
          setStep(1);
          setFormData({
            adultsCount: 1,
            adultNames: "",
            email: "",
            phone: "",
            moveInDate: "",
            moveReason: "",
            hasPets: false,
            petDetails: "",
            income: "",
            entitledToRent: false,
            consentCheck: false,
            backgroundConsent: true,
          });
        }}
      />
    </div>
  );
};

export default ApplicationFormView;
